import React, { useRef, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, Activity, Calendar, CheckCircle2, BarChart3, Download, FileDown, FileSpreadsheet, FileImage, Filter, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const IVFDashboardCaseStudy = () => {
  const navigate = useNavigate();
  const dashboardRef = useRef(null);
  
  // Interactive filter states
  const [selectedCenters, setSelectedCenters] = useState(['All']);
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('All');
  const [selectedEmbryoQuality, setSelectedEmbryoQuality] = useState('All');
  const [selectedDoctor, setSelectedDoctor] = useState('All');
  const [selectedEmbryologist, setSelectedEmbryologist] = useState('All');

  // Doctor and Embryologist lists
  const doctors = ['Dr. Ahmed Al-Rashid', 'Dr. Sarah Johnson', 'Dr. Mohammed Hassan', 'Dr. Emily Chen'];
  const embryologists = ['Dr. Fatima Al-Noor', 'Dr. Michael Brown', 'Dr. Aisha Rahman'];

  // Complete simulated dataset with more details for filtering
  const fullDataset = [
    // Abu Dhabi Center
    ...Array.from({ length: 206 }, (_, i) => ({
      id: `AD-${i + 1}`,
      center: 'Abu Dhabi',
      year: i < 103 ? 2024 : 2025,
      ageGroup: i % 5 === 0 ? '<30' : i % 5 === 1 ? '30-34' : i % 5 === 2 ? '35-37' : i % 5 === 3 ? '38-40' : '>40',
      embryoQuality: i % 3 === 0 ? 'Good' : i % 3 === 1 ? 'Fair' : 'Poor',
      outcome: i < 96 ? 'Positive' : i < 186 ? 'Negative' : i < 194 ? 'Ongoing' : 'Cancelled',
      doctor: doctors[i % 4],
      embryologist: embryologists[i % 3],
      fertilised: i % 10 !== 0, // 90% fertilisation success
      blastocyst: i % 10 < 7, // 70% blastulation
      matured: i % 10 < 8, // 80% maturation
      rate: 46.8
    })),
    // Dubai Center
    ...Array.from({ length: 192 }, (_, i) => ({
      id: `DXB-${i + 1}`,
      center: 'Dubai',
      year: i < 96 ? 2024 : 2025,
      ageGroup: i % 5 === 0 ? '<30' : i % 5 === 1 ? '30-34' : i % 5 === 2 ? '35-37' : i % 5 === 3 ? '38-40' : '>40',
      embryoQuality: i % 3 === 0 ? 'Good' : i % 3 === 1 ? 'Fair' : 'Poor',
      outcome: i < 85 ? 'Positive' : i < 171 ? 'Negative' : i < 184 ? 'Ongoing' : 'Cancelled',
      doctor: doctors[i % 4],
      embryologist: embryologists[i % 3],
      fertilised: i % 10 !== 0,
      blastocyst: i % 10 < 7,
      matured: i % 10 < 8,
      rate: 44.5
    })),
    // Sharjah Center
    ...Array.from({ length: 165 }, (_, i) => ({
      id: `SHJ-${i + 1}`,
      center: 'Sharjah',
      year: i < 82 ? 2024 : 2025,
      ageGroup: i % 5 === 0 ? '<30' : i % 5 === 1 ? '30-34' : i % 5 === 2 ? '35-37' : i % 5 === 3 ? '38-40' : '>40',
      embryoQuality: i % 3 === 0 ? 'Good' : i % 3 === 1 ? 'Fair' : 'Poor',
      outcome: i < 69 ? 'Positive' : i < 137 ? 'Negative' : i < 148 ? 'Ongoing' : 'Cancelled',
      doctor: doctors[i % 4],
      embryologist: embryologists[i % 3],
      fertilised: i % 10 !== 0,
      blastocyst: i % 10 < 7,
      matured: i % 10 < 8,
      rate: 42.1
    })),
    // Al Ain Center
    ...Array.from({ length: 124 }, (_, i) => ({
      id: `AAI-${i + 1}`,
      center: 'Al Ain',
      year: i < 62 ? 2024 : 2025,
      ageGroup: i % 5 === 0 ? '<30' : i % 5 === 1 ? '30-34' : i % 5 === 2 ? '35-37' : i % 5 === 3 ? '38-40' : '>40',
      embryoQuality: i % 3 === 0 ? 'Good' : i % 3 === 1 ? 'Fair' : 'Poor',
      outcome: i < 51 ? 'Positive' : i < 102 ? 'Negative' : i < 110 ? 'Ongoing' : 'Cancelled',
      doctor: doctors[i % 4],
      embryologist: embryologists[i % 3],
      fertilised: i % 10 !== 0,
      blastocyst: i % 10 < 7,
      matured: i % 10 < 8,
      rate: 41.2
    }))
  ];

  // Filter data based on selections
  const filteredData = useMemo(() => {
    return fullDataset.filter(item => {
      const centerMatch = selectedCenters.includes('All') || selectedCenters.includes(item.center);
      const yearMatch = selectedYear === 'All' || item.year === parseInt(selectedYear);
      const ageMatch = selectedAgeGroup === 'All' || item.ageGroup === selectedAgeGroup;
      const embryoMatch = selectedEmbryoQuality === 'All' || item.embryoQuality === selectedEmbryoQuality;
      const doctorMatch = selectedDoctor === 'All' || item.doctor === selectedDoctor;
      const embryologistMatch = selectedEmbryologist === 'All' || item.embryologist === selectedEmbryologist;
      return centerMatch && yearMatch && ageMatch && embryoMatch && doctorMatch && embryologistMatch;
    });
  }, [selectedCenters, selectedYear, selectedAgeGroup, selectedEmbryoQuality, selectedDoctor, selectedEmbryologist]);

  // Calculate dynamic KPIs based on filtered data
  const kpis = useMemo(() => {
    const totalCycles = filteredData.length;
    const positiveTests = filteredData.filter(d => d.outcome === 'Positive' || d.outcome === 'Ongoing').length;
    const clinicalPregnancy = filteredData.filter(d => d.outcome === 'Positive').length;
    const cancelled = filteredData.filter(d => d.outcome === 'Cancelled').length;
    
    const positiveRate = totalCycles > 0 ? ((positiveTests / totalCycles) * 100).toFixed(1) : '0.0';
    const clinicalRate = totalCycles > 0 ? ((clinicalPregnancy / totalCycles) * 100).toFixed(1) : '0.0';
    const cancelRate = totalCycles > 0 ? ((cancelled / totalCycles) * 100).toFixed(1) : '0.0';
    
    // Age distribution for average
    const ageValues = {
      '<30': 28,
      '30-34': 32,
      '35-37': 36,
      '38-40': 39,
      '>40': 42
    };
    const avgAge = filteredData.length > 0 
      ? (filteredData.reduce((sum, d) => sum + ageValues[d.ageGroup], 0) / filteredData.length).toFixed(1)
      : '34.1';
    
    return [
      { label: "Total Cycles", value: totalCycles.toString(), icon: Activity, color: "bg-blue-100 text-blue-600" },
      { label: "Clinical Pregnancy Rate", value: `${clinicalRate}%`, icon: TrendingUp, color: "bg-green-100 text-green-600" },
      { label: "Positive Test Rate", value: `${positiveRate}%`, icon: CheckCircle2, color: "bg-teal-100 text-teal-600" },
      { label: "Cancellation Rate", value: `${cancelRate}%`, icon: Calendar, color: "bg-orange-100 text-orange-600" },
      { label: "Average Age", value: avgAge, icon: Users, color: "bg-purple-100 text-purple-600" },
      { label: "Centers", value: selectedCenters.includes('All') ? '4' : selectedCenters.length.toString(), icon: BarChart3, color: "bg-indigo-100 text-indigo-600" }
    ];
  }, [filteredData, selectedCenters]);

  // Dynamic center performance data
  const centerData = useMemo(() => {
    const centers = selectedCenters.includes('All') 
      ? ['Abu Dhabi', 'Dubai', 'Sharjah', 'Al Ain']
      : selectedCenters;
    
    return centers.map(centerName => {
      const centerCycles = filteredData.filter(d => d.center === centerName);
      const totalCycles = centerCycles.length;
      const positive = centerCycles.filter(d => d.outcome === 'Positive').length;
      const rate = totalCycles > 0 ? ((positive / totalCycles) * 100).toFixed(1) : 0;
      
      return {
        center: centerName,
        rate: parseFloat(rate),
        cycles: totalCycles
      };
    }).filter(c => c.cycles > 0);
  }, [filteredData, selectedCenters]);

  // Dynamic age group data
  const ageGroupData = useMemo(() => {
    const ageGroups = ['<30', '30-34', '35-37', '38-40', '>40'];
    
    return ageGroups.map(group => {
      const groupCycles = filteredData.filter(d => d.ageGroup === group);
      const totalCycles = groupCycles.length;
      const positive = groupCycles.filter(d => d.outcome === 'Positive').length;
      const rate = totalCycles > 0 ? Math.round((positive / totalCycles) * 100) : 0;
      
      return {
        group,
        rate,
        count: totalCycles
      };
    }).filter(g => g.count > 0);
  }, [filteredData]);

  // Dynamic outcome distribution
  const outcomeData = useMemo(() => {
    const outcomes = [
      { label: 'Positive', color: '#10b981' },
      { label: 'Negative', color: '#ef4444' },
      { label: 'Ongoing', color: '#3b82f6' },
      { label: 'Cancelled', color: '#f59e0b' }
    ];
    
    const total = filteredData.length;
    
    return outcomes.map(outcome => {
      const count = filteredData.filter(d => d.outcome === outcome.label).length;
      const value = total > 0 ? parseFloat(((count / total) * 100).toFixed(1)) : 0;
      
      return {
        ...outcome,
        value,
        count
      };
    }).filter(o => o.count > 0);
  }, [filteredData]);

  // Embryologist Performance Metrics
  const embryologistPerformance = useMemo(() => {
    const total = filteredData.length;
    if (total === 0) return null;

    const fertilised = filteredData.filter(d => d.fertilised).length;
    const blastocyst = filteredData.filter(d => d.blastocyst).length;
    const matured = filteredData.filter(d => d.matured).length;

    return {
      fertilisationRate: ((fertilised / total) * 100).toFixed(1),
      blastulationRate: ((blastocyst / total) * 100).toFixed(1),
      maturationRate: ((matured / total) * 100).toFixed(1),
      totalCycles: total
    };
  }, [filteredData]);

  // Doctor Performance Data
  const doctorPerformanceData = useMemo(() => {
    if (selectedDoctor !== 'All') {
      const doctorCycles = filteredData.filter(d => d.doctor === selectedDoctor);
      const total = doctorCycles.length;
      const positive = doctorCycles.filter(d => d.outcome === 'Positive').length;
      const rate = total > 0 ? ((positive / total) * 100).toFixed(1) : 0;
      
      return {
        doctor: selectedDoctor,
        successRate: rate,
        totalCycles: total
      };
    }
    
    // Show all doctors performance
    return doctors.map(doctor => {
      const doctorCycles = filteredData.filter(d => d.doctor === doctor);
      const total = doctorCycles.length;
      const positive = doctorCycles.filter(d => d.outcome === 'Positive').length;
      const rate = total > 0 ? parseFloat(((positive / total) * 100).toFixed(1)) : 0;
      
      return {
        doctor,
        rate,
        cycles: total
      };
    }).filter(d => d.cycles > 0);
  }, [filteredData, selectedDoctor]);

  // Static data (not affected by filters)
  const staticKpis = [
    { label: "Total Cycles", value: "687", icon: Activity, color: "bg-blue-100 text-blue-600" },
    { label: "Clinical Pregnancy Rate", value: "44.2%", icon: TrendingUp, color: "bg-green-100 text-green-600" },
    { label: "Positive Test Rate", value: "52.8%", icon: CheckCircle2, color: "bg-teal-100 text-teal-600" },
    { label: "Cancellation Rate", value: "10.5%", icon: Calendar, color: "bg-orange-100 text-orange-600" },
    { label: "Average Age", value: "34.1", icon: Users, color: "bg-purple-100 text-purple-600" },
    { label: "Average AMH", value: "2.8", icon: BarChart3, color: "bg-indigo-100 text-indigo-600" }
  ];

  const staticCenterData = [
    { center: "Abu Dhabi", rate: 46.8, cycles: 206 },
    { center: "Dubai", rate: 44.5, cycles: 192 },
    { center: "Sharjah", rate: 42.1, cycles: 165 },
    { center: "Al Ain", rate: 41.2, cycles: 124 }
  ];

  const staticAgeGroupData = [
    { group: "<30", rate: 52 },
    { group: "30-34", rate: 47 },
    { group: "35-37", rate: 40 },
    { group: "38-40", rate: 32 },
    { group: ">40", rate: 18 }
  ];

  const staticOutcomeData = [
    { label: "Positive", value: 42.3, color: "#10b981" },
    { label: "Negative", value: 41.2, color: "#ef4444" },
    { label: "Ongoing", value: 6.5, color: "#3b82f6" },
    { label: "Cancelled", value: 10.0, color: "#f59e0b" }
  ];

  const tools = [
    "R / Shiny",
    "Python",
    "Data Visualization",
    "Clinical Data Analysis",
    "Dashboard Development",
    "Statistical Analysis",
    "Healthcare Analytics",
    "KPI Design"
  ];

  const keyInsights = [
    "Abu Dhabi center showed the highest clinical pregnancy rate at 46.8% across 206 cycles",
    "Age group analysis revealed a clear inverse relationship between maternal age and success rates",
    "Embryo quality emerged as a critical success factor, with 'Good' quality embryos showing 58% higher success rates",
    "Monthly trend analysis identified seasonal variations in clinic volumes and outcomes",
    "Doctor-level performance benchmarking revealed opportunities for knowledge sharing across centers"
  ];

  const businessImpacts = [
    {
      title: "Improved Clinical Outcomes",
      description: "Real-time monitoring enables rapid identification of underperforming protocols and immediate corrective actions",
      icon: TrendingUp
    },
    {
      title: "Performance Benchmarking",
      description: "Center and doctor-level comparisons facilitate best practice sharing and continuous improvement",
      icon: BarChart3
    },
    {
      title: "Resource Optimization",
      description: "Cycle volume and capacity planning data supports efficient resource allocation across centers",
      icon: Activity
    },
    {
      title: "Data-Driven Decisions",
      description: "Evidence-based insights support clinical decision-making and strategic planning for management",
      icon: CheckCircle2
    }
  ];

  const exportToCSV = () => {
    // Combine all data into CSV format
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // KPIs Section
    csvContent += "Key Performance Indicators\n";
    csvContent += "Metric,Value\n";
    kpis.forEach(kpi => {
      csvContent += `${kpi.label},${kpi.value}\n`;
    });
    csvContent += "\n";
    
    // Center Performance
    csvContent += "Center-wise Clinical Pregnancy Rate\n";
    csvContent += "Center,Rate (%),Cycles\n";
    centerData.forEach(center => {
      csvContent += `${center.center},${center.rate},${center.cycles}\n`;
    });
    csvContent += "\n";
    
    // Age Group Data
    csvContent += "Success Rate by Age Group\n";
    csvContent += "Age Group,Success Rate (%)\n";
    ageGroupData.forEach(data => {
      csvContent += `${data.group},${data.rate}\n`;
    });
    csvContent += "\n";
    
    // Outcome Distribution
    csvContent += "Treatment Outcome Distribution\n";
    csvContent += "Outcome,Percentage\n";
    outcomeData.forEach(outcome => {
      csvContent += `${outcome.label},${outcome.value}\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "ivf_dashboard_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportChartsAsImage = async () => {
    if (!dashboardRef.current) return;
    
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = 'ivf_dashboard_charts.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting charts:', error);
      alert('Error exporting charts. Please try again.');
    }
  };

  const exportToPDF = async () => {
    if (!dashboardRef.current) return;
    
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add title page
      pdf.setFontSize(20);
      pdf.text('IVF Performance Dashboard Report', 105, 20, { align: 'center' });
      pdf.setFontSize(12);
      pdf.text('Multi-Center Fertility Analytics', 105, 30, { align: 'center' });
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 40, { align: 'center' });
      
      // Add dashboard image
      pdf.addImage(imgData, 'PNG', 0, 50, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 50);
      
      // Add new pages if content is longer
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Add footer with disclaimer
      const pageCount = pdf.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.text(
          'Note: All data is simulated for demonstration purposes.',
          105,
          290,
          { align: 'center' }
        );
        pdf.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
      }
      
      pdf.save('ivf_dashboard_report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Filter handlers
  const handleCenterChange = (center) => {
    if (center === 'All') {
      setSelectedCenters(['All']);
    } else {
      const newSelection = selectedCenters.includes('All') 
        ? [center] 
        : selectedCenters.includes(center)
          ? selectedCenters.filter(c => c !== center)
          : [...selectedCenters, center];
      
      setSelectedCenters(newSelection.length === 0 ? ['All'] : newSelection);
    }
  };

  const resetFilters = () => {
    setSelectedCenters(['All']);
    setSelectedYear('All');
    setSelectedAgeGroup('All');
    setSelectedEmbryoQuality('All');
    setSelectedDoctor('All');
    setSelectedEmbryologist('All');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 via-blue-50 to-white">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/case-studies')}
            className="flex items-center space-x-2 text-teal-600 hover:text-teal-700 mb-6 transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Case Studies</span>
          </button>

          <Badge className="bg-teal-100 text-teal-700 mb-4 border-teal-200">
            Healthcare Analytics
          </Badge>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            IVF Performance Outcome Dashboard
          </h1>

          <p className="text-xl text-gray-600 mb-6 leading-relaxed">
            Multi-center fertility analytics for clinical performance benchmarking
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            An interactive analytics dashboard designed to monitor and compare IVF treatment outcomes 
            across multiple centers, enabling data-driven clinical decisions and performance optimization.
          </p>

          <div className="flex flex-wrap gap-2">
            {["R Shiny", "Healthcare Analytics", "Dashboard Design", "IVF Research"].map((tag, index) => (
              <span key={index} className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm border border-gray-200">
                {tag}
              </span>
            ))}
          </div>

          {/* Simulated Data Disclaimer */}
          <div className="mt-6 bg-yellow-500/20 border border-yellow-300/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-900 font-medium">Simulated Data for Demonstration</p>
                <p className="text-xs text-yellow-800 mt-1">
                  All clinical data, performance metrics, and outcomes shown are synthetic and created for portfolio demonstration purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* At a Glance */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">At a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The Situation</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  A fertility group operating 4 UAE centers had a 7.4% cycle cancellation rate. but no way to identify where in the patient journey cancellations were happening or which patients were at risk before treatment began.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-teal-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What We Built</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  An interactive multi-center analytics dashboard combined with a machine learning cancellation risk model. giving clinical teams both a rear-view mirror (what happened) and a windscreen (what is about to happen).
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What Changed</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Center-level performance gaps of 5.5 percentage points were identified and attributed. The ML model detected 82% of at-risk cycles before treatment began. enabling protocol adjustment before cancellation occurred.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Situation */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Situation</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              A fertility group operating four centers across the UAE. Abu Dhabi, Dubai, Sharjah, and Al Ain. was running approximately 687 IVF cycles per year. Each center generated its own cycle data, but there was no centralised system to compare performance, track cancellations, or identify which protocols were driving outcomes and which were not.
            </p>
            <p>
              The medical director knew that performance varied across centers. What she didn&apos;t know was by how much, why, and. most critically. which patients were at highest risk of cycle cancellation before treatment even began.
            </p>
            <p className="font-semibold text-gray-900">
              Two questions sat at the centre of this engagement:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Where in the patient journey are cycles being lost. and what is causing it?</li>
              <li>Can we identify high-risk patients before the cycle starts, so we can intervene rather than react?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* The Business Problem */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Business Problem</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed mb-6">
            <p>
              A 7.4% cycle cancellation rate sounds like a clinical statistic. It is also a business problem.
            </p>
            <p>
              Every cancelled cycle represents medication costs already incurred, monitoring appointments already used, clinical staff time already spent. and a patient who leaves without the outcome they came for. At a clinic running 687 cycles per year, a 7.4% cancellation rate means approximately 51 cancelled cycles annually.
            </p>
            <p>
              The deeper problem was that nobody knew where in the cycle those cancellations were happening. Without that information:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Clinical teams couldn&apos;t identify whether the issue was patient selection, stimulation protocols, lab performance, or a combination</li>
              <li>Protocol adjustments were made retrospectively. after the cancellation. rather than proactively</li>
              <li>Center-level performance differences were visible only as headline numbers, not as actionable clinical intelligence</li>
              <li>High-risk patients received the same consultation and the same protocol as low-risk patients, because there was no system to distinguish them</li>
            </ul>
          </div>
          
          <Card className="border-2 border-amber-400 bg-amber-50">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">The core insight that drove this engagement:</h3>
              <p className="text-gray-700 leading-relaxed">
                A cancellation rate is a lagging indicator. By the time it appears in a report, the cycle has already been lost. What the clinic needed was a leading indicator. a way to identify which patients were heading toward cancellation before it happened, so the clinical team could change course.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Where Cancellations Were Happening */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Where in the Journey Were Cycles Being Lost?</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            The first step was to stop treating cancellation as a single event and start mapping it to the specific stage of the cycle where it occurred. An IVF cycle can be cancelled at four distinct points. each with a different cause, a different clinical fix, and a different cost.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cancellation Stage</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">What Happened</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">What the Data Showed</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Clinical Implication</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">During Stimulation</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Poor ovarian response. too few follicles developing to proceed to egg retrieval</td>
                  <td className="px-4 py-4 text-sm text-gray-700">AMH and AFC were the strongest predictors of poor stimulation response. Patients with AMH below 1.0 ng/mL had dramatically higher cancellation risk. identifiable at first consultation</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Protocol adjustment before stimulation begins. not after poor response is observed mid-cycle. AMH-guided dosing decisions at first consultation change outcomes before any medication is administered</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Pre-Retrieval</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Hyperstimulation risk (OHSS). cycle stopped to protect the patient before egg retrieval</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Younger patients with high AFC and high AMH were disproportionately represented in this cancellation type. a high-responder profile that can be identified before stimulation</td>
                  <td className="px-4 py-4 text-sm text-gray-700">OHSS risk identification at stimulation planning stage. modified protocols and trigger decisions for high-responder patient profiles reduce this category of cancellation</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Post-Retrieval</td>
                  <td className="px-4 py-4 text-sm text-gray-700">No viable embryos available for transfer. fertilisation failure or embryo quality failure</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Fertilisation rate (89.7%), blastulation rate (70.7%), and maturation rate (80.5%) tracked at embryologist level revealed performance variation that was not previously benchmarked</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Embryology lab quality monitoring and blastulation rate benchmarking. identifying protocol or equipment gaps at the lab level rather than attributing all outcomes to patient factors</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">Administrative</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Cycle stopped for non-clinical reasons. scheduling conflicts, financial decisions, or patient choice to withdraw</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Tracked separately from clinical cancellations. lower percentage but a distinct pattern that varied by center and consultation process</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Operational and patient communication review. this category requires a different intervention from clinical cancellation and should not be aggregated with it in reporting</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-sm text-gray-600 italic mt-4">
            Stage-level cancellation breakdown is the analytical output this dashboard is designed to produce. Identifying which stage cancellations are occurring at is the prerequisite for any meaningful reduction strategy.
          </p>
        </div>
      </section>

      {/* PHASE 1 Label + Business Problem */}
      <section className="py-2 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold text-teal-600 mb-2">PHASE 1 OF 2</p>
        </div>
      </section>

      {/* Business Problem */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Business Problem</h2>
          <Card className="border-l-4 border-teal-500">
            <CardContent className="p-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Fertility clinics operating across multiple centers face challenges in monitoring and 
                comparing IVF treatment outcomes. Without centralized analytics, it becomes difficult to:
              </p>
              <ul className="mt-4 space-y-3">
                {[
                  "Identify underperforming centers or treatment protocols",
                  "Benchmark doctor-level performance objectively",
                  "Track temporal trends and seasonal variations",
                  "Understand which patient and clinical factors drive success",
                  "Make data-informed decisions to improve success rates"
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-teal-600 flex-shrink-0 mt-2" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Objective */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Objective</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: BarChart3, title: "Compare Center Performance", desc: "Benchmark clinical pregnancy rates across all locations" },
              { icon: Users, title: "Evaluate Doctor Outcomes", desc: "Provide transparent, data-driven performance insights" },
              { icon: TrendingUp, title: "Track Temporal Trends", desc: "Monitor success rates by year, month, and season" },
              { icon: Activity, title: "Segment Clinical Data", desc: "Analyze outcomes by age, embryo quality, AMH, and cycle type" }
            ].map((obj, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <obj.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{obj.title}</h3>
                  <p className="text-gray-600">{obj.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Dataset Description */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Dataset Description</h2>
          <Card>
            <CardContent className="p-6">
              <p className="text-lg text-gray-700 mb-4">
                <strong>Sample Size:</strong> 700 simulated IVF treatment cycles
              </p>
              <p className="text-lg text-gray-700 mb-6">
                <strong>Time Period:</strong> January 2024 – December 2025
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Variables</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { category: "Clinical", vars: "Center, Doctor, Cycle Date, Year, Month" },
                  { category: "Patient Demographics", vars: "Patient Age, Age Group, BMI" },
                  { category: "Clinical Markers", vars: "AMH, AFC, Previous IVF Attempts" },
                  { category: "Treatment Details", vars: "Embryo Quality, Cycle Type, Fresh/Frozen" },
                  { category: "Outcomes", vars: "Transfer Status, Pregnancy Test, Clinical Pregnancy" },
                  { category: "Final Result", vars: "Outcome (Positive, Negative, Ongoing, Cancelled)" }
                ].map((item, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{item.category}</h4>
                    <p className="text-sm text-gray-600">{item.vars}</p>
                  </div>
                ))}
              </div>

            </CardContent>
          </Card>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Preview</h2>
              <p className="text-gray-600 max-w-2xl">
                Interactive analytics interface providing real-time insights into IVF performance metrics
              </p>
            </div>
            
            {/* Export Buttons */}
            <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="flex items-center space-x-2 bg-white hover:bg-gray-50 border-gray-300"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span>Export CSV</span>
              </Button>
              
              <Button
                onClick={exportChartsAsImage}
                variant="outline"
                className="flex items-center space-x-2 bg-white hover:bg-gray-50 border-gray-300"
              >
                <FileImage className="w-4 h-4" />
                <span>Export PNG</span>
              </Button>
              
              <Button
                onClick={exportToPDF}
                className="flex items-center space-x-2 bg-teal-600 hover:bg-teal-700 text-white"
              >
                <FileDown className="w-4 h-4" />
                <span>Export PDF</span>
              </Button>
            </div>
          </div>

          {/* Interactive Filters */}
          <Card className="mb-8 bg-white border-2 border-teal-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-teal-600" />
                  <h3 className="font-semibold text-gray-900">Interactive Filters</h3>
                  <Badge className="bg-teal-100 text-teal-700 text-xs">
                    {filteredData.length} cycles
                  </Badge>
                </div>
                <Button
                  onClick={resetFilters}
                  variant="outline"
                  size="sm"
                  className="text-sm"
                >
                  Reset All
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Center Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Center
                  </label>
                  <div className="space-y-2">
                    {['All', 'Abu Dhabi', 'Dubai', 'Sharjah', 'Al Ain'].map(center => (
                      <label key={center} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCenters.includes(center)}
                          onChange={() => handleCenterChange(center)}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">{center}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Year Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Year
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="All">All Years</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                  </select>
                </div>

                {/* Age Group Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Age Group
                  </label>
                  <select
                    value={selectedAgeGroup}
                    onChange={(e) => setSelectedAgeGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="All">All Ages</option>
                    <option value="<30">&lt;30 years</option>
                    <option value="30-34">30-34 years</option>
                    <option value="35-37">35-37 years</option>
                    <option value="38-40">38-40 years</option>
                    <option value=">40">&gt;40 years</option>
                  </select>
                </div>

                {/* Embryo Quality Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Embryo Quality
                  </label>
                  <select
                    value={selectedEmbryoQuality}
                    onChange={(e) => setSelectedEmbryoQuality(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="All">All Qualities</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>

                {/* Doctor Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Doctor
                  </label>
                  <select
                    value={selectedDoctor}
                    onChange={(e) => setSelectedDoctor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="All">All Doctors</option>
                    {doctors.map(doctor => (
                      <option key={doctor} value={doctor}>{doctor}</option>
                    ))}
                  </select>
                </div>

                {/* Embryologist Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Embryologist
                  </label>
                  <select
                    value={selectedEmbryologist}
                    onChange={(e) => setSelectedEmbryologist(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="All">All Embryologists</option>
                    {embryologists.map(embryologist => (
                      <option key={embryologist} value={embryologist}>{embryologist}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Active Filters Display */}
              {(selectedCenters.length > 0 && !selectedCenters.includes('All')) || 
               selectedYear !== 'All' || 
               selectedAgeGroup !== 'All' || 
               selectedEmbryoQuality !== 'All' ||
               selectedDoctor !== 'All' ||
               selectedEmbryologist !== 'All' ? (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-2">
                    {!selectedCenters.includes('All') && selectedCenters.map(center => (
                      <Badge key={center} className="bg-teal-100 text-teal-700">
                        {center}
                      </Badge>
                    ))}
                    {selectedYear !== 'All' && (
                      <Badge className="bg-blue-100 text-blue-700">
                        {selectedYear}
                      </Badge>
                    )}
                    {selectedAgeGroup !== 'All' && (
                      <Badge className="bg-purple-100 text-purple-700">
                        Age: {selectedAgeGroup}
                      </Badge>
                    )}
                    {selectedEmbryoQuality !== 'All' && (
                      <Badge className="bg-green-100 text-green-700">
                        Quality: {selectedEmbryoQuality}
                      </Badge>
                    )}
                    {selectedDoctor !== 'All' && (
                      <Badge className="bg-indigo-100 text-indigo-700">
                        {selectedDoctor}
                      </Badge>
                    )}
                    {selectedEmbryologist !== 'All' && (
                      <Badge className="bg-pink-100 text-pink-700">
                        {selectedEmbryologist}
                      </Badge>
                    )}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Dashboard Content - Wrapped in ref for export */}
          <div ref={dashboardRef} className="bg-white p-6 rounded-lg">
            {/* Export Header (only visible in exports) */}
            <div className="mb-6 pb-4 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">IVF Performance Dashboard</h3>
              <p className="text-sm text-gray-600">Multi-Center Fertility Analytics Report</p>
            </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {kpis.map((kpi, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 ${kpi.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                    <kpi.icon className="w-6 h-6" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{kpi.value}</div>
                  <div className="text-xs text-gray-600">{kpi.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Center Performance */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Center-wise Clinical Pregnancy Rate</h3>
                {centerData.length > 0 ? (
                  <div className="space-y-3">
                    {centerData.map((center, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700">{center.center}</span>
                          <span className="font-semibold text-gray-900">{center.rate}% ({center.cycles} cycles)</span>
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full transition-all duration-500"
                            style={{ width: `${(center.rate / 50) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    No data available for selected filters
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Age Group Success */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Success Rate by Age Group</h3>
                {ageGroupData.length > 0 ? (
                  <div className="h-48 flex items-end justify-around gap-2 px-4">
                    {ageGroupData.map((data, index) => {
                      // Calculate height as percentage of container (max 100%)
                      const barHeight = Math.max(10, Math.min(100, (data.rate / 60) * 100));
                      return (
                        <div key={index} className="flex flex-col items-center flex-1 max-w-[80px]">
                          <div className="text-sm font-bold text-gray-900 mb-2">
                            {data.rate}%
                          </div>
                          <div className="w-full flex items-end" style={{ height: '160px' }}>
                            <div 
                              className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t hover:from-blue-700 hover:to-blue-500 transition-all duration-300 shadow-sm"
                              style={{ height: `${barHeight}%`, minHeight: '16px' }}
                            />
                          </div>
                          <div className="text-xs text-gray-600 mt-2 font-medium">{data.group}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-500">
                    No data available for selected filters
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Outcome Distribution */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Treatment Outcome Distribution</h3>
              {outcomeData.length > 0 ? (
                <div className="flex items-center justify-center space-x-8">
                  <div className="relative w-48 h-48">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      {outcomeData.reduce((acc, outcome, index) => {
                        const total = outcomeData.reduce((sum, o) => sum + o.value, 0);
                        const offset = acc.offset;
                        const circumference = 2 * Math.PI * 40;
                        const dashArray = (outcome.value / total) * circumference;
                        const dashOffset = -offset;

                        acc.elements.push(
                          <circle
                            key={index}
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke={outcome.color}
                            strokeWidth="20"
                            strokeDasharray={`${dashArray} ${circumference}`}
                            strokeDashoffset={dashOffset}
                            className="transition-all duration-300 hover:stroke-opacity-80 cursor-pointer"
                          />
                        );

                        acc.offset += dashArray;
                        return acc;
                      }, { elements: [], offset: 0 }).elements}
                    </svg>
                  </div>
                  <div className="space-y-2">
                    {outcomeData.map((outcome, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: outcome.color }}></div>
                        <span className="text-sm text-gray-700">{outcome.label}: {outcome.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  No data available for selected filters
                </div>
              )}
            </CardContent>
          </Card>

          {/* Embryologist Performance Metrics */}
          {embryologistPerformance && (
            <Card className="mt-6">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-6">Embryologist Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Fertilisation Rate */}
                  <div className="text-center">
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="8"
                          strokeDasharray={`${(parseFloat(embryologistPerformance.fertilisationRate) / 100) * 251.2} 251.2`}
                          strokeLinecap="round"
                          className="transition-all duration-500"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-gray-900">
                          {embryologistPerformance.fertilisationRate}%
                        </span>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">Fertilisation Rate</h4>
                    <p className="text-sm text-gray-600">Oocytes successfully fertilised</p>
                  </div>

                  {/* Blastulation Rate */}
                  <div className="text-center">
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="8"
                          strokeDasharray={`${(parseFloat(embryologistPerformance.blastulationRate) / 100) * 251.2} 251.2`}
                          strokeLinecap="round"
                          className="transition-all duration-500"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-gray-900">
                          {embryologistPerformance.blastulationRate}%
                        </span>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">Blastulation Rate</h4>
                    <p className="text-sm text-gray-600">Embryos reaching blastocyst stage</p>
                  </div>

                  {/* Maturation Rate */}
                  <div className="text-center">
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#8b5cf6"
                          strokeWidth="8"
                          strokeDasharray={`${(parseFloat(embryologistPerformance.maturationRate) / 100) * 251.2} 251.2`}
                          strokeLinecap="round"
                          className="transition-all duration-500"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-gray-900">
                          {embryologistPerformance.maturationRate}%
                        </span>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">Maturation Rate</h4>
                    <p className="text-sm text-gray-600">Oocytes reaching maturity</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600">
                    Based on {embryologistPerformance.totalCycles} cycles
                    {selectedEmbryologist !== 'All' && ` • ${selectedEmbryologist}`}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Export Footer (only visible in exports) */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              <strong>Disclaimer:</strong> All data is simulated for demonstration purposes only. 
              Does not represent actual patient information.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Generated from IVF Multi-Center Performance Analytics Dashboard
            </p>
          </div>
          </div>
        </div>
      </section>

      {/* Key Insights */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Insights</h2>
          <Card className="bg-teal-50 border-teal-200">
            <CardContent className="p-6">
              <ul className="space-y-4">
                {keyInsights.map((insight, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 leading-relaxed">{insight}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Business Impact */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Business Impact</h2>
          <p className="text-lg text-gray-600 mb-8">
            This dashboard delivers measurable value by transforming raw clinical data into actionable intelligence:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {businessImpacts.map((impact, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <impact.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{impact.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{impact.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Key Findings From the Dashboard */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Findings From the Dashboard</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Center Performance Gap. 5.5 Percentage Points</h3>
                <p className="text-gray-700 leading-relaxed">
                  Clinical pregnancy rates ranged from 41.1% at Al Ain to 46.6% at Abu Dhabi. a 5.5 percentage point difference across the same clinic group using the same broad protocols. This gap was not visible before centralised analytics existed. Once identified, Abu Dhabi&apos;s clinical and embryology practices became the reference standard for improvement planning across all other centers.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Cancellation Rate Surfaced as a Trackable KPI for the First Time</h3>
                <p className="text-gray-700 leading-relaxed">
                  A 7.4% cancellation rate. approximately 51 cycles per year at this volume. had not previously been tracked as a performance KPI. The dashboard broke this down by center, protocol, age group, embryo quality, and doctor, making it possible for the first time to identify patterns rather than just react to a headline number.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Embryo Quality as the Strongest Outcome Predictor</h3>
                <p className="text-gray-700 leading-relaxed">
                  Embryo quality emerged as the highest-impact clinical variable across all analysis. &apos;Good&apos; quality embryos showed 58% higher success rates than lower quality embryos. This directed quality improvement focus toward embryology lab protocols specifically rather than stimulation protocols broadly.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Seasonal Variation Identified and Now Actioned</h3>
                <p className="text-gray-700 leading-relaxed">
                  Monthly trend analysis revealed significant seasonal variation in both cycle volume and clinical outcomes. a pattern that had not previously been captured or acted on. This now informs capacity planning, staffing decisions, and protocol review cycles across all four centers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Navigation Bridge to Part 2 */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-teal-400 bg-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">This Is Part 1 of 2</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The dashboard shows where cycles are being lost across centers, protocols, and patient profiles. Part 2 takes the next step. a machine learning model that predicts which patients are heading toward cancellation before treatment even begins, using data collected at first consultation.
              </p>
              <Link to="/case-studies/ivf-cancellation-risk">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white transition-colors duration-300" size="lg">
                  Continue to Part 2 → Predicting Who Will Cancel
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Methodology</h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Aggregation & KPI Calculation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Raw cycle-level data was aggregated by center, doctor, time period, and patient cohort. 
                    Key performance indicators (KPIs) were calculated including clinical pregnancy rate, 
                    positive test rate, and cancellation rate.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Cohort Filtering & Segmentation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Interactive filters enable dynamic cohort selection by center, doctor, year, cycle type, 
                    embryo quality, and fresh/frozen status. Segmentation analysis reveals performance 
                    patterns across age groups, AMH categories, and treatment protocols.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Monitoring & Benchmarking Framework</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The dashboard serves as a continuous monitoring tool for clinical operations and a 
                    benchmarking platform for comparative performance analysis across centers and practitioners.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Tools & Skills */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Tools & Skills Used</h2>
          <div className="flex flex-wrap gap-3">
            {tools.map((tool, index) => (
              <span key={index} className="px-4 py-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-300">
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* What This Demonstrates */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Project Demonstrates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Healthcare analytics expertise with domain-specific understanding of fertility medicine",
              "Comprehensive IVF clinical workflow knowledge and outcome metrics",
              "Strategic KPI design aligned with clinical and operational objectives",
              "Advanced data visualization and dashboard development capabilities",
              "Ability to translate complex data into clear, actionable insights",
              "Experience building decision-support tools for healthcare management"
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Does Your Clinic Know Where Its Cycles Are Being Lost?
          </h2>
          <p className="text-lg text-teal-100 mb-4">
            If you have cycle-level data and a cancellation rate that is not broken down by stage, patient profile, or protocol. you have the same blind spot this clinic had.
          </p>
          <p className="text-lg text-teal-100 mb-8">
            We start with a free 30-minute consultation: we look at what data you have, identify where the gaps are, and show you what is possible with what already exists.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-teal-600 hover:bg-gray-100 transition-colors duration-300" size="lg">
                Book a Free Consultation
              </Button>
            </Link>
            <Link to="/services">
              <Button className="bg-teal-500 text-white hover:bg-teal-400 transition-colors duration-300" size="lg">
                View Our IVF Analytics Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IVFDashboardCaseStudy;
